import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import { getPollMs } from '../utils/appSettings';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);
const API = (p) => `${config.API_BASE_URL}${p}`;
const COURSES = { 1: tr('Starters'), 2: tr('Mains'), 4: tr('Sides'), 5: tr('Drinks'), 3: tr('Desserts') };

const ageMin = (sentAt) => Math.max(0, Math.floor((Date.now() - new Date(sentAt).getTime()) / 60000));

// Screen-only station queue (kitchen / bar). No printers by design.
const StationScreen = ({ station: stationProp, embedded = false }) => {
  const { station: stationParam } = useParams();
  const station = stationProp || stationParam;
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(API(`/ordering/station/${station}`));
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { /* next poll retries */ }
  }, [station]);
  useEffect(() => { load(); const t = setInterval(load, getPollMs('normal')); return () => clearInterval(t); }, [load]);

  const setStatus = async (id, status) => {
    try { await axios.put(API(`/ordering/order-items/${id}/status`), { status }); load(); }
    catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };

  // group by order
  const groups = [];
  const byOrder = new Map();
  rows.forEach((r) => {
    if (!byOrder.has(r.order)) { byOrder.set(r.order, []); groups.push(r.order); }
    byOrder.get(r.order).push(r);
  });

  // Embedded στην Κουζίνα (η λίστα Kitchen View είναι από κάτω): όταν μια κράτηση
  // έχει ΜΟΝΟ ένα πιάτο στην ουρά, τα per-item κουμπιά κρύβονται — το Done/Served
  // της γραμμής στη λίστα τα καλύπτει (bulk), ώστε να μην υπάρχουν διπλά χειριστήρια.
  const perReservation = new Map();
  rows.forEach((r) => {
    if (r.reservation) perReservation.set(r.reservation, (perReservation.get(r.reservation) || 0) + 1);
  });
  const buttonsHidden = (it) =>
    embedded && it.reservation && perReservation.get(it.reservation) === 1;

  if (embedded && rows.length === 0) return null;
  return (
    <div style={{ width: embedded ? '100%' : '96%', margin: embedded ? '0 0 14px' : '10px auto' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h2 style={{ margin: '6px 0', fontSize: embedded ? 19 : undefined }}>
          {embedded ? tr('Orders queue') :
            ({ bar: tr('Bar screen'), bbq: tr('BBQ screen'), salads: tr('Salads screen'),
               desserts: tr('Desserts screen') }[station] || tr('Kitchen screen'))} ({rows.length})
        </h2>
      </div>
      {!embedded && rows.length === 0 && <p style={{ opacity: 0.6 }}>{tr('No pending items.')}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' }}>
        {groups.map((oid) => {
          const items = byOrder.get(oid);
          const age = ageMin(items[0].sent_at);
          return (
            <div key={oid} style={{
              border: '1px solid #ccc', borderRadius: 10, minWidth: 260, maxWidth: 330, flex: '1 1 260px',
              borderTop: `5px solid ${age >= 15 ? '#b02a37' : age >= 8 ? '#d6a400' : '#198754'}`,
            }}>
              <div style={{ display: 'flex', padding: '6px 10px', borderBottom: '1px solid #eee', gap: 8 }}>
                <b>{tr('Table')} {items[0].tables || '—'}</b>
                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{age}'</span>
              </div>
              {items.map((it) => (
                <div key={it.id} style={{ padding: '6px 10px', borderBottom: '1px dashed #eee' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <b>{it.qty}×</b>
                    <span style={{ flex: 1 }}>{it.item_name}</span>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{it.course_name || COURSES[it.course] || ''}</span>
                  </div>
                  {it.note && <div style={{ fontSize: 12, color: '#b02a37' }}>✎ {it.note}</div>}
                  <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                    {!buttonsHidden(it) && (
                      <>
                        {it.status === 'sent' && (
                          <button className="rlToolbarBtn" style={{ padding: '3px 10px' }}
                            onClick={() => setStatus(it.id, 'preparing')}>{tr('preparing')}</button>
                        )}
                        {it.status !== 'ready' && (
                          <button className="rlToolbarBtn" style={{ padding: '3px 10px' }}
                            onClick={() => setStatus(it.id, 'ready')}>{tr('ready')}</button>
                        )}
                        {it.status === 'ready' && (
                          <button className="rlToolbarBtn rlToolbarBtnPrimary" style={{ padding: '3px 10px' }}
                            onClick={() => setStatus(it.id, 'served')}>{tr('served')}</button>
                        )}
                      </>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: 11, alignSelf: 'center', opacity: 0.65 }}>
                      {buttonsHidden(it) ? `${tr(it.status)} · ${tr('via the list below')}` : tr(it.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StationScreen;
